import VideoPlayer from "@dana/Components/VideoPlayer";
import Youtube from "@dana/MobiusOverrides/Icons/Youtube";
import { themeBackgrounds } from "@dana/MobiusOverrides/styles/colors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img, { ImgProps } from "@insite/mobius/Img";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { css } from "styled-components";

interface SplLiteDisclaimerScreenProps {
    fields: any;
    onProceedToNextScreen?: () => void;
}

export interface SplLiteDisclaimerStyles {
    wrapper?: InjectableCss;
    heading?: TypographyPresentationProps;
    contentWrapper?: InjectableCss;
    actionWrapper?: InjectableCss;
    bulletinReference?: ButtonPresentationProps;
    image?: ImgProps;
    disclaimerVideoModal?: ModalPresentationProps;
    watchVideoButton?: ButtonPresentationProps;
}

export const splLiteDisclaimerStyles: SplLiteDisclaimerStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 70%;
            gap: 24px;
            margin: 18px auto 0 auto;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 100%;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },
    heading: {
        variant: "h3",
    },
    contentWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;
            gap: 16px;
            border: 1px solid ${themeBackgrounds.lightGray};
            border-radius: 4px;
            padding: 16px;
            box-sizing: border-box;
        `,
    },
    image: {
        css: css`
        width: 100%;
        height: auto;
        max-height: 480px;
        object-fit: contain;
        ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            max-height: 180px;
                        `,
                        css`
                            max-height: 220px;
                        `,
                        css`
                            max-height: 260px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )
            }
    `,
    },
    actionWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            width: 100%;
            gap: 16px;
        `,
    },
    disclaimerVideoModal: {
        cssOverrides: {
            modalContent: css`
            video {
                width: 100%;
                height: auto;
                max-height: 70vh;
                display: block;
            }
        `,
        },
        sizeVariant: "large",
    },
    bulletinReference: {
        variant: "tertiary",
        css: css`
            align-self: flex-start;
            max-width: 400px;
            text-align: left;
            justify-content: flex-start;
            display: flex;
            width: auto;
            background-color: #fff00f;
        `,
    },
    watchVideoButton: {
        variant: "primary",
        icon: {
            position: "left",
            src: () => <Youtube />
        },
    },
};

const SplLiteDisclaimerScreen: React.FC<SplLiteDisclaimerScreenProps> = ({
    fields, onProceedToNextScreen
}) => {
    const styles = splLiteDisclaimerStyles;
    const [disclaimerVideoModalIsOpen, setDisclaimerVideoModalIsOpen] = useState(false);
    const disclaimerVideoUrl = useGetLink(fields.splLiteDisclaimerVideo);
    const disclaimerVideoCaption = useGetLink(fields.splLiteDisclaimerVideoCaption);
    const bulletinUrl = useGetLink(fields.bulletinReferenceLink);

    const history = useHistory();
    const { isInShell } = useShellContext();

    const onBulletinClick = () => {
        if (!bulletinUrl?.url) {
            return;
        }

        if (bulletinUrl.url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.open(bulletinUrl.url, "_blank", "noopener,noreferrer");
        } else {
            history.push(bulletinUrl.url);
        }
    };

    const disclaimerVideoModalCloseHandler = () => {
        setDisclaimerVideoModalIsOpen(false);
    };

    const stopWizard = () => { window.location.href = "/"; };

    return (
        <StyledWrapper>
            <StyledWrapper {...styles.wrapper} className="wrapper">
                <Typography {...styles.heading} as="h3">
                    {fields.splLiteDisclaimerHeading}
                </Typography>
                <StyledWrapper {...styles.contentWrapper}>
                    {fields.bulletinReferenceText && fields.bulletinReferenceLink && (
                        <Button {...styles.bulletinReference} variant="tertiary" onClick={onBulletinClick}>
                            {fields.bulletinReferenceText}
                        </Button>
                    )}
                    {fields.splLiteDisclaimerImage && (
                        <Img src={fields.splLiteDisclaimerImage} altText={fields.splLiteDisclaimerImageAltText || ''} {...styles.image} />
                    )}
                    {disclaimerVideoUrl?.url && (
                        <Button {...styles.watchVideoButton} onClick={() => setDisclaimerVideoModalIsOpen(true)}>
                            {fields.watchVideoLabel}
                        </Button>
                    )}
                    <StyledWrapper {...styles.actionWrapper}>
                        <Button variant="secondary" onClick={() => stopWizard()}>
                            {fields.stopLabel}
                        </Button>
                        <Button variant="primary" onClick={onProceedToNextScreen}>
                            {fields.continueLabel}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>

            </StyledWrapper>
            <Modal
                {...styles.disclaimerVideoModal}
                id="splLiteDisclaimerVideoModal"
                isOpen={disclaimerVideoModalIsOpen}
                handleClose={disclaimerVideoModalCloseHandler}>
                <StyledWrapper>
                    <VideoPlayer
                        videoUrl={disclaimerVideoUrl?.url || ''}
                        videoCaptionUrl={disclaimerVideoCaption?.url || ''}
                    />
                </StyledWrapper>
            </Modal>
        </StyledWrapper>
    )
};

export default SplLiteDisclaimerScreen;